import { faCircleSmall } from '@fortawesome/pro-solid-svg-icons';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Divider } from 'src/components/Divider';
import { ExpandableText } from 'src/components/ExpandableText';
import { Icon } from 'src/components/Icon';
import { Link } from 'src/components/Link';
import { ListItem } from 'src/components/ListItem';
import { Stack } from 'src/components/Stack';
import { Sticky } from 'src/components/Sticky';
import { Typography } from 'src/components/Typography';
import { fetchResources as fetchAppointmentResources } from 'src/models/appointment/appointment';
import { getAppointmentStaff } from 'src/models/appointment/utils';
import { getUserDisplayName } from 'src/models/user/utils';
import { DrawerTab, DrawerTabs } from 'src/shared/DrawerTabs';
import { ImageOrLogo } from 'src/shared/ImageOrLogo';
import { usePermissions } from 'src/shared/PermissionCheck';
import {
  formatMonthDayYear,
  formatStartAndEndTime,
} from 'src/utils/dateFormatter';

import { AppointmentCardSection } from '../AppointmentCardSection';

import { AppointmentMembersSection } from './AppointmentMembersSection';
import { ZoomListItem } from './ZoomListItem';

export const TrainerAppointmentDetails = ({
  appointment,
  setVisit,
  isAppointmentFuture,
  handleEditDetailsClick,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.appointmentDetailsTrainer',
  });

  const [activeTab, setActiveTab] = useState('visits');
  const { data: appointmentResources, isLoading: appointmentResourcesLoading } =
    useQuery({
      queryKey: ['appointment_resources', appointment?.id],

      queryFn: () => {
        if (!appointment) return [];

        return fetchAppointmentResources(appointment);
      },
    });

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Stack pb={1} spacing={2} direction="row" alignItems="center">
          <ImageOrLogo
            imageUrl={appointment.service.thumbnail}
            height={66}
            width={80}
          />
          <Stack spacing={1}>
            <Typography variant="h6">{appointment.serviceName}</Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <Box>{formatMonthDayYear(appointment.startTime)}</Box>
              <Box>
                {formatStartAndEndTime(
                  appointment.startTime,
                  appointment.endTime
                )}
              </Box>
              <Box>
                {appointment.trainerNames.join(', ')}
                {appointment.location && ` in ${appointment.location.name}`}
              </Box>
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <Sticky>
        <ZoomListItem appointment={appointment} />
        <TrainerTabs
          onChange={(value) => setActiveTab(value)}
          activeTab={activeTab}
          appointment={appointment}
        />
      </Sticky>
      {activeTab == 'details' && (
        <Box>
          <AppointmentCardSection
            showIf={!!appointment.notes}
            title={
              <Stack direction="row" spacing={1} alignItems="center">
                <Box>{t('notes')}</Box>
                <Icon sx={{ width: '.75rem' }} icon={faCircleSmall} />
              </Stack>
            }
          >
            <ExpandableText sx={{ pb: 1 }} showMore={true} lines={3}>
              <Typography variant="body2" color="text.secondary">
                {appointment.notes}
              </Typography>
            </ExpandableText>
          </AppointmentCardSection>
          <AppointmentCardSection
            showIf={!!appointment.description}
            title={t('description')}
          >
            <ExpandableText sx={{ pb: 1 }} showMore={true} lines={3}>
              <Typography variant="body2" color="text.secondary">
                {appointment.description}
              </Typography>
            </ExpandableText>
          </AppointmentCardSection>

          <AppointmentCardSection
            showIf={!appointmentResourcesLoading && appointmentResources.length}
            title={t('assets')}
          >
            <ExpandableText sx={{ pb: 1 }} showMore={true} lines={3}>
              <Typography variant="body2" color="text.secondary">
                {!appointmentResourcesLoading &&
                  appointmentResources.length > 0 &&
                  appointmentResources
                    .map((resource) => resource.name)
                    .join(', ')}
              </Typography>
            </ExpandableText>
          </AppointmentCardSection>

          <AppointmentCardSection disableChildPadding sx={{ pt: 1 }}>
            {getAppointmentStaff(appointment).map((s) => (
              <ListItem
                key={s.user.id}
                avatar={<Avatar src={s.user.profileImage} />}
                primary={getUserDisplayName(s.user)}
                secondary={
                  s.isPrimary ? t('primaryStaff') : t('secondaryStaff')
                }
              />
            ))}
          </AppointmentCardSection>

          <Divider />

          <AppointmentCardSection
            showIf={isAppointmentFuture}
            disableBorder
            sx={{ pt: 2 }}
          >
            <Link onClick={handleEditDetailsClick} variant="body2">
              <Box>{t('editSessionDetails')}</Box>
            </Link>
          </AppointmentCardSection>
          <AppointmentCardSection disableBorder sx={{ pt: 2 }}>
            <Link
              href={`/fbm/services/${appointment.serviceId}`}
              target="_blank"
              variant="body2"
            >
              <Box>{t('editGlobalServiceSettings')}</Box>
            </Link>
          </AppointmentCardSection>
        </Box>
      )}
      {activeTab !== 'details' && (
        <AppointmentMembersSection
          appointment={appointment}
          setVisit={setVisit}
          showWaitlist={activeTab === 'waitlist'}
        />
      )}
    </>
  );
};

const TrainerTabs = ({ appointment, activeTab, onChange }) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.tabs',
  });
  const { permissionCheck } = usePermissions();

  return (
    <DrawerTabs activeTab={activeTab} onChange={onChange}>
      <DrawerTab value="visits" label={t('attendees')} />
      {permissionCheck({ can: 'viewAppointmentMembers' }) &&
        appointment.service?.waitlist && (
          <DrawerTab value="waitlist" label={t('waitlist')} />
        )}
      <DrawerTab
        value="details"
        label={
          appointment.notes ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Box>{t('sessionDetails')}</Box>
              <Icon sx={{ width: '.75rem' }} icon={faCircleSmall} />
            </Stack>
          ) : (
            t('sessionDetails')
          )
        }
      />
    </DrawerTabs>
  );
};
